import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { Calendar } from 'src/components/ui/calendar';
import MarkdownEditor from 'src/components/common/MarkdownEditor';
import {
  markdownToHTML,
  htmlToMarkdown,
} from 'src/components/common/MarkdownViewer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import {
  ArrowRightCircle,
  Settings,
  UserPlus,
  UserCheck,
  Handshake,
  Upload,
  Building2,
  Calendar as CalendarIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useAuth } from 'src/hooks/useAuth';
import SelectPartnerModal from 'src/components/Modals/SelectPartnerModal';
import EngageInstitutionModal from 'src/components/Modals/EngageInstitutionModal';
import ForwardToTaModal from 'src/components/Modals/ForwardToTaModal';

const OpportunityActions = ({
  opportunityId,
  isAssigned,
  isClaimable,
  onClaim,
  isClaiming,
  isPublished,
  publishInitialValues,
  onAssignToTa,
  onAssignPartner,
  onPublishAssignment,
  routingStatus,
  deadline,
  locationId,
  assId,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getUserInfo } = useAuth();
  const [showForwardTaModal, setShowForwardTaModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showEngageInstitutionModal, setShowEngageInstitutionModal] =
    useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [deadlinePopoverOpen, setDeadlinePopoverOpen] = useState(false);
  const [publishForm, setPublishForm] = useState({
    title: '',
    additionalDetails: '',
    deadline: '',
  });
  // Job Description is markdown source everywhere outside this modal (it's
  // rendered elsewhere via a markdown parser), but MarkdownEditor speaks
  // HTML in/out — so its live editing value is kept separate here and only
  // converted back to markdown right before it leaves the modal (on
  // submit), never on every keystroke. Feeding a re-derived value back into
  // MarkdownEditor on each change would fight its own lastSynced DOM-sync
  // guard and reset the cursor mid-typing.
  const [jobDescriptionHtml, setJobDescriptionHtml] = useState('');

  const user = getUserInfo();
  const isCse = user?.roles?.includes('ahedge_cse');
  const isTa = user?.roles?.includes('ahedge_ta');
  const requirementLocationId =
    searchParams.get('requirementLocationId') || locationId;

  // Get assignmentId from search params for TA users
  const assignmentId = searchParams.get('assignmentId') || assId;

  const toInputDate = (value) => {
    if (!value) return '';
    const dateObj = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return '';
    return dateObj.toISOString().slice(0, 10);
  };

  const toApiDeadline = (value) => {
    if (!value) return '';
    if (value.includes('/')) return value;
    const [year, month, day] = value.split('-');
    if (!year || !month || !day) return '';
    return `${day}/${month}/${year}`;
  };

  const toDisplayDeadline = (value) => {
    if (!value) return '';
    const isoDate = toInputDate(value);
    if (!isoDate) return '';
    return toApiDeadline(isoDate);
  };

  const isValidDeadlineFormat = (value) => /^\d{2}\/\d{2}\/\d{4}$/.test(value);

  // Convert dd/mm/yyyy to Date object
  const parseDeadlineToDate = (value) => {
    if (!value || !isValidDeadlineFormat(value)) return undefined;
    const [day, month, year] = value.split('/');
    return new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10)
    );
  };

  // Convert Date object to dd/mm/yyyy
  const formatDeadlineFromDate = (date) => {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime()))
      return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Parse initial deadline from various formats to Date object
  const parseInitialDeadline = (value) => {
    if (!value) return null;
    // If it's already in dd/mm/yyyy format
    if (typeof value === 'string' && value.includes('/')) {
      if (isValidDeadlineFormat(value)) {
        return parseDeadlineToDate(value);
      }
    }
    // Try parsing as ISO date or other formats
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }
    return null;
  };

  // Get the maximum date (initial deadline) for Calendar restriction
  const getMaxDeadlineDate = () => {
    const initialDeadline = parseInitialDeadline(
      publishInitialValues?.deadline
    );
    if (initialDeadline) {
      const maxDate = new Date(initialDeadline);
      maxDate.setHours(0, 0, 0, 0);
      return maxDate;
    }
    return null;
  };

  useEffect(() => {
    if (!showPublishModal) return;
    setPublishForm({
      title: publishInitialValues?.title || '',
      additionalDetails: publishInitialValues?.additionalDetails || '',
      deadline: toDisplayDeadline(publishInitialValues?.deadline),
    });
    setJobDescriptionHtml(
      markdownToHTML(publishInitialValues?.jobDescription || '')
    );
  }, [showPublishModal, publishInitialValues]);

  // MarkdownEditor's value is HTML; job_description is stored/consumed as
  // markdown everywhere else, so emptiness has to be checked on the
  // rendered text content, not the raw HTML string (which is non-empty
  // even for e.g. "<p><br></p>").
  const isJobDescriptionEmpty = (html) => {
    if (!html) return true;
    const container = document.createElement('div');
    container.innerHTML = html;
    return !container.textContent.trim();
  };

  const handlePartnerSelection = (data) => {
    setShowPartnerModal(false);
    if (onAssignPartner) {
      onAssignPartner(data);
    }
  };

  const handlePublish = async () => {
    if (!assignmentId || !onPublishAssignment) return;
    const jobDescriptionMarkdown = htmlToMarkdown(jobDescriptionHtml).trim();
    if (
      !publishForm.title.trim() ||
      !jobDescriptionMarkdown ||
      !publishForm.additionalDetails.trim() ||
      !isValidDeadlineFormat(publishForm.deadline) ||
      !publishForm.deadline
    ) {
      return;
    }

    try {
      setIsPublishing(true);
      const isSuccess = await onPublishAssignment({
        title: publishForm.title.trim(),
        job_description: jobDescriptionMarkdown,
        additional_details: publishForm.additionalDetails.trim(),
        deadline: toApiDeadline(publishForm.deadline),
      });
      if (isSuccess) {
        setShowPublishModal(false);
      }
    } finally {
      setIsPublishing(false);
    }
  };

  // Descriptors, not JSX — lets renderActionButton() decide whether to lay
  // these out as dropdown items or (when there's only one) as a single
  // direct button, off the same data instead of two parallel code paths.
  const getMenuItems = () => {
    const menuItems = [];

    // Unclaimed requirement (no CSE owns it yet) — the one action that
    // makes sense before anything else here does, so it goes first.
    // Mutually exclusive with the Forward-to-TA item below in practice:
    // isClaimable only holds while nobody's claimed it, and isAssigned
    // (Forward-to-TA's own gate) only holds once someone has.
    if (isCse && isClaimable) {
      menuItems.push({
        key: 'claim-opportunity',
        icon: UserCheck,
        label: isClaiming ? 'Claiming...' : 'Claim',
        onClick: onClaim,
        disabled: isClaiming,
      });
    }

    // CSE forwards a requirement directly to the open TA pool — no TA Lead
    // step, no specific TA target.
    if (isCse && isAssigned && routingStatus !== 'ASSIGNED') {
      menuItems.push({
        key: 'forward-ta-direct',
        icon: ArrowRightCircle,
        label: 'Forward to TA',
        onClick: () => setShowForwardTaModal(true),
      });
    }

    if (isTa) {
      menuItems.push({
        key: 'assign-partner',
        icon: Handshake,
        label: 'Assign Partner',
        onClick: () => setShowPartnerModal(true),
      });

      if (!isPublished) {
        menuItems.push({
          key: 'publish',
          icon: Upload,
          label: 'Publish',
          onClick: () => setShowPublishModal(true),
        });
      } else {
        menuItems.push({
          key: 'engage-institution',
          icon: Building2,
          label: 'Engage Institution',
          onClick: () => setShowEngageInstitutionModal(true),
        });
      }
    }

    return menuItems;
  };

  const menuItems = getMenuItems();

  // Check if TA user has only one action (Submit Candidates)
  const isTaWithSingleAction = isTa && menuItems.length === 1;

  const renderActionButton = () => {
    if (isTaWithSingleAction) {
      return (
        <Button
          size="sm"
          variant="secondary"
          className="flex items-center gap-2 border bg-background w-full"
          onClick={() => {
            const submitUrl = `/candidates/submit?opportunityId=${opportunityId}${assignmentId ? `&assignmentId=${assignmentId}` : ''}${requirementLocationId ? `&requirementLocationId=${requirementLocationId}` : ''}`;
            navigate(submitUrl);
          }}
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Submit Candidates
        </Button>
      );
    }

    if (!menuItems.length) return null;

    // A single available action gets its own highlighted button instead of
    // an Actions dropdown that only ever opens to one choice — same reasons
    // as the TA single-action case above, generalized to any role/state.
    if (menuItems.length === 1) {
      const [only] = menuItems;
      const OnlyIcon = only.icon;
      return (
        <Button
          variant="primary"
          size="headerSm"
          className="gap-1.5"
          onClick={only.onClick}
          disabled={only.disabled}
        >
          <OnlyIcon className="h-4 w-4" />
          {only.label}
        </Button>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="headerSm" className="gap-1.5">
            <Settings className="h-4 w-4" />
            Actions
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-[200px]">
          {menuItems.map(({ key, icon: Icon, label, onClick, disabled }) => (
            <DropdownMenuItem
              key={key}
              onClick={onClick}
              disabled={disabled}
              className="cursor-pointer "
            >
              <Icon className="mr-2 h-4 w-4" />
              <span>{label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      {renderActionButton()}

      <ForwardToTaModal
        isOpen={showForwardTaModal}
        onClose={() => setShowForwardTaModal(false)}
        onSubmit={onAssignToTa}
        opportunityId={opportunityId}
        deadline={deadline}
      />

      <SelectPartnerModal
        isOpen={showPartnerModal}
        onClose={() => setShowPartnerModal(false)}
        onSubmit={handlePartnerSelection}
        opportunityId={opportunityId}
        assignmentId={assignmentId}
      />

      <Dialog open={showPublishModal} onOpenChange={setShowPublishModal}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Publish Assignment</DialogTitle>
          </DialogHeader>

          <DialogBody className="grid gap-4 max-h-[75vh]">
            <div>
              <div className="mb-1 text-xs font-semibold text-foreground">
                title
              </div>
              <Input
                id="publish-job-title"
                value={publishForm.title}
                onChange={(event) =>
                  setPublishForm((prev) => ({
                    ...prev,
                    title: event.target.value,
                  }))
                }
                placeholder="Enter title"
              />
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold text-foreground">
                Job Description
              </div>
              <MarkdownEditor
                value={jobDescriptionHtml}
                onChange={setJobDescriptionHtml}
                minHeight={220}
                maxHeight={280}
                title="Job Description"
              />
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold text-foreground">
                Additional Details
              </div>
              <Textarea
                id="publish-additional-details"
                value={publishForm.additionalDetails}
                onChange={(event) =>
                  setPublishForm((prev) => ({
                    ...prev,
                    additionalDetails: event.target.value,
                  }))
                }
                rows={4}
                placeholder="Enter additional details"
              />
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold text-foreground">
                Deadline
              </div>
              <Popover
                open={deadlinePopoverOpen}
                onOpenChange={setDeadlinePopoverOpen}
              >
                <PopoverTrigger asChild>
                  <div className="relative">
                    <div
                      className={`flex h-9 w-full items-center rounded-md border-0 px-3 py-2 pr-10 text-sm shadow-sm ring-1 ring-inset ring-input cursor-pointer ${
                        publishForm.deadline
                          ? 'text-foreground-strong'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {publishForm.deadline || 'Select date'}
                    </div>
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parseDeadlineToDate(publishForm.deadline)}
                    onSelect={(selectedDate) => {
                      if (selectedDate) {
                        setPublishForm((prev) => ({
                          ...prev,
                          deadline: formatDeadlineFromDate(selectedDate),
                        }));
                        setDeadlinePopoverOpen(false);
                      }
                    }}
                    disabled={(date) => {
                      const maxDate = getMaxDeadlineDate();
                      if (maxDate) {
                        const dateToCheck = new Date(date);
                        dateToCheck.setHours(0, 0, 0, 0);
                        return dateToCheck > maxDate;
                      }
                      return false;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPublishModal(false)}
              disabled={isPublishing}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={
                isPublishing ||
                !assignmentId ||
                !publishForm.title.trim() ||
                isJobDescriptionEmpty(jobDescriptionHtml) ||
                !publishForm.additionalDetails.trim() ||
                !isValidDeadlineFormat(publishForm.deadline) ||
                !publishForm.deadline
              }
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EngageInstitutionModal
        isOpen={showEngageInstitutionModal}
        onClose={() => setShowEngageInstitutionModal(false)}
        opportunityId={opportunityId}
        assignmentId={assignmentId}
      />
    </>
  );
};

export default OpportunityActions;
